/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment'
import React, { useEffect, useState, useRef } from 'react'
import DatePicker from 'react-date-picker'
import 'react-date-picker/dist/DatePicker.css'
import { createUseStyles } from 'react-jss'
import Input from './Input'
import { Calendar as CalendarIcon, Close } from '../../../assets/icons'
import { baseButton } from '../../../theme/reuseables'
import { colors } from '../../../theme/palette'

type RuleNames =
    | 'datepickerWrapper'
    | 'datepicker'
    | 'button'
    | 'trigger'
    | 'close'

type Props = {
    label: string
    selected: Date | undefined | null
    setSelected: (date: Date | undefined | null) => void
    maxDate?: Date
    minDate?: Date
    disableCalendar?: boolean
    hasError?: boolean
}

interface StyleProps {
    hasValue?: boolean
    isDatePicker?: boolean
}

export const useStyles = createUseStyles<RuleNames, StyleProps, Jss.Theme>({
    datepickerWrapper: {
        border: 'none',
        position: 'relative',
        color: colors.black,
        '& label > input': {
            color: ({ hasValue }) => {
                return hasValue ? 'transparent' : 'black'
            },
        },
    },
    datepicker: {
        position: 'absolute',
        zIndex: 2,
        height: '100%',
        width: '100%',
        top: '0',
        right: '0',
        textAlign: 'right',
        border: `none`,
        borderRadius: '6px',
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
            padding: 0,
            margin: 0,
        },
    },
    trigger: {
        position: 'absolute',
        top: '16px',
        right: '10px',
        opacity: '.8',
    },
    close: {
        position: 'absolute',
        top: '18px',
        right: '28px',
    },
})

const DatePickerComponent: React.FC<Props> = ({
    label,
    selected,
    setSelected,
    maxDate,
    minDate,
    disableCalendar,
    hasError,
}) => {
    const [dateValue, setdateValue] = useState(undefined)
    const [showPicker, setShowPicker] = useState(false)
    const [hasValue, setHasValue] = useState(false)
    const [inputValues, setInputValues] = useState({
        day: '',
        month: '',
        year: '',
    })
    const classes = useStyles({
        ...{ hasValue },
    })

    useEffect(() => {
        if (dateValue && dateValue.length !== 0) {
            setHasValue(true)
        } else {
            setHasValue(false)
        }
    }, [dateValue])

    useEffect(() => {
        if (!selected) {
            setdateValue(undefined)
        }
    }, [selected])

    const handleOnChangeDate = (val: any): void => {
        if (val && typeof val === 'object') {
            const asText = moment(val).format('DD.MM.YYYY')
            setdateValue(asText)
        } else {
            // Handle null/undefined case (clear button)
            setdateValue(undefined)
        }
        setShowPicker(false)
        setSelected(val)
    }

    const handleInputChange = (element: HTMLInputElement): void => {
        const { name, value } = element
        if (name) {
            setInputValues((prev) => ({
                ...prev,
                [name.replace('react-date-picker_', '')]: value,
            }))
        }
    }

    const setupInputListeners = (elements: HTMLInputElement[]): void => {
        // Find the parent div with class react-date-picker__inputGroup
        const inputGroup = elements[0]?.closest(
            '.react-date-picker__inputGroup'
        )
        if (inputGroup) {
            // Get all input elements inside this div
            const inputs = inputGroup.querySelectorAll('input')

            inputs.forEach((input) => {
                input.addEventListener('input', () => {
                    handleInputChange(input as HTMLInputElement)
                })
            })
        }
    }

    return (
        <div className={classes.datepickerWrapper}>
            <Input
                label={label}
                required={false}
                disabled={showPicker}
                value={dateValue || ''}
                isDatePicker
            />
            <div
                className={classes.datepicker}
                style={{
                    cursor: 'pointer',
                    border: `1px solid ${
                        hasError ? colors.error : colors.borderColor
                    }`,
                    borderRadius: '5px',
                }}
            >
                <DatePicker
                    disableCalendar={disableCalendar}
                    // onActiveStartDateChange={(val) => {
                    //     console.log('change', val)
                    // }}
                    onChange={handleOnChangeDate}
                    value={selected}
                    maxDate={maxDate}
                    minDate={minDate}
                    calendarIcon={
                        <CalendarIcon width="16px" color={colors.green500} />
                    }
                    format="dd.MM.y"
                    dayPlaceholder="dd"
                    monthPlaceholder="mm"
                    yearPlaceholder="åååå"
                    clearIcon={<Close width="16px" color={colors.green500} />}
                    inputRef={(elements) => {
                        if (elements && Array.isArray(elements)) {
                            setupInputListeners(elements)
                        }
                    }}
                />
            </div>
        </div>
    )
}

export { DatePickerComponent }
